import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAI } from '../../context/AIContext';
import { useAuth } from '../../context/AuthContext';
import EnterpriseNavigation from './EnterpriseNavigation';
import Header from './Header';
import ComingSoon from '../common/ComingSoon';
import Breadcrumb from '../common/Breadcrumb';

const EnterpriseLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user } = useAuth();

  // Use location.pathname directly instead of state to ensure it's always current
  const currentPage = location.pathname;

  const handleNavigate = (path) => {
    if (path.startsWith('/coming-soon')) {
      navigate(path);
    } else {
      navigate(path);
    }
    // No need to set currentPage since we're using location.pathname directly
  };

  // Check if current page should show coming soon
  const shouldShowComingSoon = () => {
    const comingSoonPaths = ['/insights/reports'];
    return comingSoonPaths.includes(location.pathname);
  };

  const getComingSoonFeature = () => {
    const pathMap = {
      '/insights/reports': { id: 'reports', name: 'Reports' },
    };
    return pathMap[location.pathname] || { id: 'general', name: 'Feature' };
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const { aiEnabled } = useAI();

  useEffect(() => {
    // If AI has been disabled while user is on an AI route, redirect to dashboard and notify
    const path = location.pathname || '';
    if (
      !aiEnabled &&
      (path.startsWith('/ai') || path.startsWith('/intelligence'))
    ) {
      navigate('/dashboard');
      toast.info('AI disabled — redirected to Dashboard');
    }
  }, [aiEnabled, location.pathname, navigate]);

  // Protect routes based on user.assignedMenus.
  // If assignedMenus is non-empty, derive the top-level section from the pathname
  // (e.g. /collaboration/hub -> /collaboration, /intelligence -> /ai) and redirect
  // the user to their first allowed section when they attempt to access a non-allowed path.
  useEffect(() => {
    const assignedMenus = Array.isArray(user?.assignedMenus) ? user.assignedMenus : [];
    if (!assignedMenus || assignedMenus.length === 0) return;

    const deriveSection = (pathname) => {
      if (!pathname || pathname === '/') return '/overview';
      const parts = pathname.split('/');
      const first = parts[1] ? `/${parts[1]}` : '/overview';
      // Normalize intelligence -> /ai (UserManagement stores '/ai')
      if (first === '/intelligence') return '/ai';
      if (first === '/dashboard') return '/overview';
      return first;
    };

    const section = deriveSection(location.pathname);
    if (!assignedMenus.includes(section)) {
      const redirectTo = assignedMenus[0] || '/overview';
      if (location.pathname !== redirectTo) {
        navigate(redirectTo, { replace: true });
        toast.info('You do not have access to that section. Redirected to an allowed page.');
      }
    }
  }, [location.pathname, navigate, user]);

  if (shouldShowComingSoon()) {
    const feature = getComingSoonFeature();
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          fontFamily: 'var(--font-family)',
        }}
      >
        <EnterpriseNavigation
          activePage={location.pathname}
          onNavigate={handleNavigate}
          isOpen={sidebarOpen}
        />
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Header
            onToggleSidebar={handleToggleSidebar}
            sidebarOpen={sidebarOpen}
          />
          <div style={{ flex: 1, overflow: 'auto' }}>
            <div style={{ padding: '16px 24px 0' }}>
              <Breadcrumb />
            </div>
            <ComingSoon
              featureName={feature.name}
              featureId={feature.id}
              onBack={handleBack}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      }}
    >
      <EnterpriseNavigation
        activePage={location.pathname}
        onNavigate={handleNavigate}
        isOpen={sidebarOpen}
      />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Header
          onToggleSidebar={handleToggleSidebar}
          sidebarOpen={sidebarOpen}
        />
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#F6FAFF',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default EnterpriseLayout;
